import clsx from 'clsx';
import { useEffect } from 'react';

import type * as api from '@gitbook/api-client';
import {
    EditableCodeBlock,
    type EditableCodeBlockProps,
    PlainCodeBlock,
} from '@gitbook/react-uikit';
import { LoadingIndicator } from '@gitbook/spine-react';
import {
    useDebouncedFlush,
    useEventCallback,
    useLoadingState,
    usePrevious,
} from '@gitbook/util-react-hooks';

import { resolveDynamicBinding } from './bindings';
import { ContentKitButton } from './ContentKitButton';
import { ensureStackElements } from './ContentKitStack';
import type { ContentKitElementProps } from './types';

export function ContentKitCodeBlock(
    props: ContentKitElementProps<api.ContentKitCodeBlock>
): React.ReactElement {
    const { element, localState, setLocalState, dispatchAction, renderElements } = props;

    const stateKey = element.state;
    const defaultValue = resolveDynamicBinding(localState, element.content);
    const editableValue = stateKey ? String(localState[stateKey] || '') : undefined;

    const commonProps = {
        value: editableValue || defaultValue,
        syntax: element.syntax || 'plain',
        lineNumbers: element.lineNumbers,
        header:
            element.header && element.header.length > 0 ? (
                <div className="py-6">
                    {renderElements('block', ensureStackElements('vstack', element.header))}
                </div>
            ) : null,
        footer:
            element.footer && element.footer.length > 0 ? (
                <div className="pt-4 pb-6">
                    {renderElements('block', ensureStackElements('vstack', element.footer))}
                </div>
            ) : null,
        toolbar:
            element.buttons && element.buttons.length > 0
                ? element.buttons.map((button, index) => (
                      <ContentKitButton
                          key={index}
                          {...props}
                          element={button}
                          buttonProps={{
                              kind: 'secondary',
                              size: 'medium',
                          }}
                      />
                  ))
                : undefined,
        showToolbar: element.buttons && element.buttons.length > 0,
    };

    if (stateKey) {
        return (
            <ContentKitEditableCodeBlock
                onChange={(newValue) => {
                    setLocalState((prev) => ({
                        ...prev,
                        [stateKey]: newValue,
                    }));
                }}
                onSave={async () => {
                    if (element.onContentChange) {
                        await dispatchAction(element.onContentChange);
                    }
                }}
                {...commonProps}
            />
        );
    }

    return <PlainCodeBlock code={commonProps.value} {...commonProps} />;
}

function ContentKitEditableCodeBlock(
    props: EditableCodeBlockProps & {
        onSave: () => Promise<void>;
    }
) {
    const { onSave, toolbar, ...rest } = props;
    const [loading, wrapLoading] = useLoadingState();
    const save = useEventCallback(() => {
        wrapLoading(onSave);
    });
    const previousValue = usePrevious(props.value);
    const debounce = useDebouncedFlush(300);
    useEffect(() => {
        if (previousValue && previousValue !== props.value) {
            debounce(save);
        }
    }, [props.value, previousValue, debounce, save]);
    return (
        <EditableCodeBlock
            {...rest}
            toolbar={
                <>
                    {toolbar}
                    <LoadingIndicator
                        size={16}
                        className={clsx('transition', loading ? 'opacity-100' : 'opacity-0')}
                    />
                </>
            }
            showToolbar
            onBlur={() => {
                debounce.flush();
            }}
        />
    );
}
